<script lang="ts">
  import { onMount, onDestroy } from 'svelte';

  const phrases = [
    "Your Azimuth agent attacks your protocol and finds exploits before they do",
    "Constantly pressure test your contracts",
    "Your contracts are now more secure through constant pressure testing",
    "Watch Azimuth attack a smart contract in real time",
    "Find the exploit before they do",
    "We break your contracts so attackers can't",
    "Every protocol has a weakness. Find yours first.",
    "What attackers see before they strike. Now you see it first",
    "Your audit ends. Azimuth never stops.",
    "Security that doesn't sleep between deployments",
    "Your contracts, under attack. On your terms.",
    "Red team your contracts at machine speed",
    "Ship knowing your contracts have already been attacked",
    "Stop waiting to be exploited",
    "The only way to know is to attack"
  ];

  // Fisher-Yates shuffle so phrases appear in random order each visit
  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  let queue = shuffle(phrases);
  let currentIndex = 0;
  let visible = true;
  let cycleInterval: ReturnType<typeof setInterval>;

  onDestroy(() => clearInterval(cycleInterval));
  // Scoped CSS (all under .azimuth-live). Order mirrors the original app:
  // theme vars first, then transcript, then shell overrides, then scan panel.
  import '$lib/azimuth-live/styles/pi-theme.css';
  import '$lib/azimuth-live/styles/pi-transcript.css';
  import '$lib/azimuth-live/styles/styles.css';
  import '$lib/azimuth-live/styles/scan-panel.css';

  // The client (d3 + elkjs + marked + highlight.js) is heavy and browser-only,
  // so it's dynamically imported in onMount — off the critical path, never run
  // during prerender. The shell markup below renders immediately (prerendered)
  // and fills with live data once mounted.
  onMount(() => {
    // On very small phones the graph is too cramped to be useful; skip the
    // heavy live client (d3/elkjs/SSE) entirely there.
    cycleInterval = setInterval(() => {
      visible = false;
      setTimeout(() => {
        currentIndex++;
        if (currentIndex >= queue.length) {
          queue = shuffle(phrases);
          currentIndex = 0;
        }
        visible = true;
      }, 400);
    }, 4000);

    if (window.matchMedia('(max-width: 360px)').matches) return;

    let teardown: (() => void) | undefined;
    let cancelled = false;
    import('$lib/azimuth-live/main')
      .then(({ mountAzimuthLive }) => {
        if (!cancelled) teardown = mountAzimuthLive();
      })
      .catch((err) => console.error('azimuth-live failed to mount:', err));
    return () => {
      cancelled = true;
      teardown?.();
    };
  });
</script>

<section class="azimuth-live" aria-label="Live Azimuth protocol scan">
  <!-- Hero statement above the live viz. -->
  <div class="hero-bar">
    <h1 class="hero-title" class:fade-out={!visible}>{queue[currentIndex]}</h1>
    <p class="hero-subtitle"><span class="hero-arrow" aria-hidden="true">↓</span> Watch it in action <span class="hero-arrow" aria-hidden="true">↓</span></p>
  </div>
  <div class="shell">
    <section class="pane graph-pane">
      <div class="graph-wrap">
        <div id="protocol-graph" class="protocol-graph"></div>
        <!-- Decorative red scan sweep; overlays the graph SVG (DOM order). -->
        <div class="graph-scan" aria-hidden="true"></div>
        <!-- Attack agent rendered as a node that docks onto the contract it's
             working on (positioned/tethered by scan-panel.ts). -->
        <div id="attack-ship" class="attack-ship" aria-hidden="true">
          <div class="attack-ship-head"><span class="attack-ship-dot"></span>Azimuth</div>
          <div id="attack-ship-msg" class="attack-ship-msg"></div>
        </div>
        <!-- Intentionally empty: the scan-panel client picks the correct stage
             (Constructing / Loading next protocol / …) and animates it in once
             scan/current resolves, so no placeholder text flashes on load. -->
        <div id="graph-overlay" class="graph-overlay"></div>
        <!-- Active protocol name + graph stats, overlaid bottom-right of the graph. -->
        <div id="scan-title" class="scan-title">Waiting for next protocol…</div>
        <div id="graph-meta" class="graph-meta"></div>
      </div>
    </section>
    <div class="left-col">
      <section class="pane chat-pane">
        <div class="pane-header"><h2>Researching potential targets...</h2><span class="live-tag"><span class="live-dot" aria-hidden="true"></span>Live</span></div>
        <div id="transcript" class="transcript pi-transcript"></div>
      </section>
    </div>
    <div class="right-col">
      <section class="pane activity-pane">
        <div class="pane-header"><h2>Azimuth Attack Agent</h2><span class="live-tag"><span class="live-dot" aria-hidden="true"></span>Live</span></div>
        <div id="activity-log" class="activity-log"></div>
      </section>
    </div>
    <!-- Idle-only indicator beside the centered investigation agent: signals
         that the system is waiting on the next scan. Fades out once one loads. -->
    <div class="scan-loading" aria-hidden="true">
      <span class="scan-loading-dots"><i></i><i></i><i></i></span>
      <span>Hunting for whales</span>
    </div>
  </div>
</section>
